import { useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { GogaillePlacesOfDestination } from "~/applications/OfferSearch/Domain/GogaillePlacesOfDestination";
import { contentStudioClient } from "~/clients/ContentStudio";
import { buildGroqQuery } from "~/core/api/Sanity/query";

const BuildingSchema = z.object({
  address: z.object({
    line1: z.string(),
    line2: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
    coordinate: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
});

const DestinationsPayloadSchema = z.object({
  logePages: z.array(
    z.object({
      seoSlug: z.object({
        current: z.string(),
      }),
      loge: z.object({
        pmsId: z.object({
          current: z.string(),
        }),
        name: z.string(),
        building: BuildingSchema,
      }),
    }),
  ),
});

const getGogaillePlacesOfDestinationQuery = () =>
  buildGroqQuery(`
{
  "logePages": (
    *[
        _type == "logePage"
        && references(
            *[
                _type == "loge"
                && references($destinationId)
            ]._id
        )
        && !(_id in path('drafts.**'))
    ]{
        seoSlug{ current },
        loge->{
            pmsId{ current },
            name,
            building->{
            address{
                line1,
                line2,
                postalCode,
                city,
                coordinate{ lat, lng }
            }
            }
        }
    } | order(loge.name asc)
  )
}
`);

async function fetchGogaillePlacesOfDestination(
  destinationId: string,
): Promise<GogaillePlacesOfDestination> {
  const response = await contentStudioClient.fetch(
    getGogaillePlacesOfDestinationQuery(),
    {
      destinationId,
    },
  );

  const gogaillePlacesPayload = DestinationsPayloadSchema.parse(response);

  return {
    destinationId,
    loges: gogaillePlacesPayload.logePages.map((oneLogePagePayload) => ({
      logeId: oneLogePagePayload.loge.pmsId.current,
      name: oneLogePagePayload.loge.name,
      building: {
        address: {
          line1: oneLogePagePayload.loge.building.address.line1,
          line2: oneLogePagePayload.loge.building.address.line2 ?? undefined,
          postalCode: oneLogePagePayload.loge.building.address.postalCode,
          city: oneLogePagePayload.loge.building.address.city,
          coordinate: {
            latitude: oneLogePagePayload.loge.building.address.coordinate.lat,
            longitude: oneLogePagePayload.loge.building.address.coordinate.lng,
          },
        },
      },
    })),
  };
}

export const useGogaillePlacesOfDestination = (destinationId: string) => {
  const { data: gogaillePlaces = null } = useQuery(
    ["gogaille-places-of-destination", destinationId],
    () => fetchGogaillePlacesOfDestination(destinationId),
  );

  return { gogaillePlaces };
};
